import { AssetActivity, Asset, User, UserActivity } from 'src/types'

const maxDistanceForNearby = 10 // in km

const forComparation = (string: string) =>
  string.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export const filterAssets = (query: string, location?: number[]): (asset: Asset) => boolean => {
  const q = forComparation(query)

  return (asset: Asset) => {
    if (location) {
      if (!asset.lat || !asset.lon) return false
      const dist = haversineDistance(
        location[1], location[0],
        parseFloat(asset.lon), parseFloat(asset.lat))
      if (dist > maxDistanceForNearby) return false
    }
    const asText = [
      asset.activeID,
      asset.slug,
      asset.prinexId,
      asset.address,
      asset.city,
      asset.province,
      asset.zipCode,
      asset.subscribers?.map(a => a.code).join(' ')
    ].join(' ').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    return forComparation(asText).includes(q)
  }
}

export const filterUsers = (query: string): (user: User) => boolean => {
  const q = forComparation(query)
  return (user: User) => {
    const asText = [
      user.userId,
      user.name,
      user.phone,
      user.mail,
      user.contractor,
      ...(user.provinces || [])
    ].join(' ').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    return forComparation(asText).includes(q)
  }
}

export const filterAssetActivities = (query: string): (activity: AssetActivity) => boolean => {
  const q = forComparation(query)
  return (activity: AssetActivity) => {
    const asText = [
      activity.action,
      activity.fullname,
      activity.address
    ].join(' ').toLowerCase()
    return forComparation(asText).includes(q)
  }
}

export const filterUserActivities = (query: string): (activity: UserActivity) => boolean => {
  const q = forComparation(query)
  return (activity: UserActivity) => {
    const asText = [
      activity.action,
      activity.address
    ].join(' ')
    return forComparation(asText).includes(q)
  }
}

/**
 * Given two coord pairs, gives the distance "as the crow flies" between them, in Km
 * @returns Distance in Km
 */
export const haversineDistance = (lon1: number, lat1: number, lon2: number, lat2: number): number => {
  const R = 6371

  const x1 = lat2 - lat1
  const dLat = x1 * Math.PI / 180
  const x2 = lon2 - lon1
  const dLon = x2 * Math.PI / 180
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const d = R * c
  return d
}
